import { useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { DEBOUNCE_DELAY } from '@lumapps/constants';

type DebounceSettings = Parameters<typeof debounce>[2];

/**
 * The default options for the debounce function.
 * To keep the reference stable, it must be declared separately and not inline
 */
const DEFAULT_OPTIONS: DebounceSettings = {};

/**
 * Debounce an object or string of any type.
 *
 * @param value     Value to be debounced.
 * @param delay     Delay to wait before updating value.
 * @param options   Options to apply to debounce function. If used, the object should be memoized for the debounce to work properly
 * @returns Debounced value.
 */
export const useDebounceValue = <T = unknown>(
    value: T,
    delay = DEBOUNCE_DELAY,
    options: DebounceSettings = DEFAULT_OPTIONS,
) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    const updateDebouncedValue = useMemo(() => debounce(setDebouncedValue, delay, options), [delay, options]);

    useEffect(() => {
        updateDebouncedValue(value);
    }, [updateDebouncedValue, value]);

    return debouncedValue;
};
